import { useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import MainContentLayout from "../../components/mainContentLayout";
import PageHeader from "../../components/pageHeader";
import { API_BASE } from "../../config/apiConfig";
import LoadingOverlay from "../../components/loadingOverlay";
import { fromFirestoreTimestamp, toLocalInputValue, formatDateTime } from "../../utils/date";
import { useToast } from "../../hooks/useToast";
import { useDispatch, useSelector } from "react-redux";
import { updateLog } from "../../features/ptLogs/ptLogsSlice";
import { FaCheck, FaTimes } from "react-icons/fa";

export default function EditPTLog() {
  const { id } = useParams();
  const token = sessionStorage.getItem("access_token");
  const user = useSelector((state) => state.user.user);
  const isCoach = Array.isArray(user.role)
    ? user.role.includes("coach") || user.role.includes("admin")
    : user.role === "coach" || user.role === "admin";

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const { log: passedLog } = state || {};

  const { showToast, ToastContainer } = useToast();

  if (!passedLog) {
    return <p className="p-4 text-red-600">Invalid navigation — no log provided.</p>;
  }

  // Convert timestamp
  const jsDate = fromFirestoreTimestamp(passedLog.date);

  // ------------------------------
  // FORM STATES
  // ------------------------------
  const [date, setDate] = useState(toLocalInputValue(jsDate));
  const [answers, setAnswers] = useState(passedLog.answers || []);
  const [comment, setComment] = useState(passedLog.coachComment || "");
  const [loading, setLoading] = useState(false);

  // Only player uses this validation
  const allFilled = answers.every(a => a.answer.trim() !== "");

  function handleAnswerChange(i, value) {
    setAnswers(prev =>
      prev.map((a, idx) => (idx === i ? { ...a, answer: value } : a))
    );
  }

  const from = state?.from;

  // ------------------------------
  // SUBMIT HANDLER (Coach OR Player)
  // ------------------------------
  async function handleSubmit(e) {
    e.preventDefault();

    if (!isCoach && !allFilled) return; // player protection only

    setLoading(true);

    try {
      let url = "";
      let payload = {};

      if (isCoach) {
        // ========= COACH COMMENT UPDATE ============
        url = `${API_BASE}/api/pt-log/${id}/comment`;
        payload = { comment };
      } else {
        // ========= PLAYER EDIT PT LOG ==============
        url = `${API_BASE}/api/pt-log/${id}`;
        payload = {
          date: new Date(date).toISOString(),
          answers,
        };
      }

      const res = await fetch(url, {
        method: isCoach ? "PATCH" : "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!data.success) {
        showToast(data.message || "Failed to update log.", "error");
        return;
      }

      // ------------------------------
      // REDUX UPDATE
      // ------------------------------
      const updatedJsDate = new Date(date);
      const key = `player-${passedLog.userId}-${updatedJsDate.getFullYear()}-${updatedJsDate.getMonth() + 1}`;

      if (isCoach) {
        // COACH COMMENT UPDATE ONLY
        dispatch(updateLog({
          oldKey: key,       // stays the same month
          newKey: key,       // same key → doesn’t move months
          logId: id,
          updatedLog: {
            isCommented: true,
            commentedAt: {
              _seconds: Math.floor(Date.now() / 1000),
              _nanoseconds: 0,
            },
            coachComment: comment,
          },
        }));

      } else {
        // PLAYER EDITING DATE — may change month
        const oldKey = `player-${user.user_id}-${jsDate.getFullYear()}-${jsDate.getMonth() + 1}`;
        const newKey = `player-${user.user_id}-${updatedJsDate.getFullYear()}-${updatedJsDate.getMonth() + 1}`;

        dispatch(updateLog({
          oldKey,
          newKey,
          logId: id,
          updatedLog: {
            date: {
              _seconds: Math.floor(updatedJsDate.getTime() / 1000),
              _nanoseconds: 0,
            },

            // Preserve coach fields
            isCommented: passedLog.isCommented || false,
            coachComment: passedLog.coachComment || "",
            commentedAt: passedLog.commentedAt || null,
          },
        }));
      }

      showToast("Updated successfully!", "success");

      if (isCoach) {
        navigate(`/view-pt-log/${passedLog.createdBy}/${id}`, { state: { from } });
      } else {
        navigate(`/view-pt-log/${id}`);
      }

    } catch (err) {
      console.error("Update PT log error:", err);
      showToast("Network error.", "error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <MainContentLayout>
      <PageHeader
        title="Edit PT Log"
        backTo={isCoach ? `/view-pt-log/${passedLog.createdBy}/${id}` : `/view-pt-log/${id}`}
        stateToPass={{ from }}
      />
      <LoadingOverlay show={loading} />
      <ToastContainer />

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex justify-end gap-4 mb-4">
          <button
            type="submit"
            disabled={!isCoach && !allFilled}
            className={`${
              (!isCoach && !allFilled)
                ? "text-gray-300 cursor-not-allowed"
                : "text-blue-600 hover:text-blue-800"
            }`}
            title="Save Changes"
          >
            <FaCheck size={22} />
          </button>

          <button
            type="button"
            onClick={() => {
              if (isCoach) {
                navigate(`/view-pt-log/${passedLog.createdBy}/${id}`, { state: { from } });
              } else {
                navigate(`/view-pt-log/${id}`);
              }
            }}
            className="text-gray-500 hover:text-gray-700"
            title="Cancel"
          >
            <FaTimes size={22} />
          </button>
        </div>

        {isCoach && (
          <div className="mt-6 text-left">
            <p className="font-bold text-sm mb-1">Coach’s Comment</p>
            <textarea
              rows="3"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="border p-2 w-full placeholder:italic placeholder:text-gray-400"
              placeholder="Write your comment here"
            />
          </div>
        )}

        {/* DATE FIELD (Player only) */}
        {!isCoach && (
          <div>
            <label className="block mb-1">Date & Time</label>
            <input
              type="datetime-local"
              value={date}
              onChange={e => setDate(e.target.value)}
              className="border p-2 w-full"
            />
          </div>
        )}

        {/* ANSWERS SECTION */}
        <div className="space-y-6">
          {answers.map((item, idx) => (
            <div key={idx}>
              <p className="font-bold text-sm mb-1 text-left">
                {idx + 1}. {item.question} <span className="text-red-500">*</span>
              </p>

              <textarea
                rows="3"
                value={item.answer}
                onChange={(e) => !isCoach && handleAnswerChange(idx, e.target.value)}
                className={`border p-2 w-full ${isCoach ? "bg-gray-100" : ""}`}
                disabled={isCoach}
              />
            </div>
          ))}
        </div>

        {!isCoach && (
          <div className="mt-6 text-left">
            <p className="font-bold text-sm mb-1">Coach’s Comment</p>
            <textarea
              rows="3"
              value={comment}
              disabled
              className="border p-2 w-full bg-gray-100"
            />

            {/* Commented Date (show only if exists) */}
            {passedLog.commentedAt && (
              <p className="text-xs text-gray-500 mt-1">
                Commented on:{" "}
                {formatDateTime(passedLog.commentedAt)}
              </p>
            )}
          </div>
        )}
      </form>
    </MainContentLayout>
  );
}
